import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/store';
import { documentSelectOneObjectByQuery, knowledgebaseSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import RelevantDocumentCard from '../common/RelevantDocumentCard';

interface DocumentSearchResultItemProps {
  documentId: string;
  relevance: string;
  contentType: string;
  score?: number;
  confidence?: number;
  reasoning?: string;
  keyTerms?: string[];
}

/**
 * Container component for DocumentSearchResultItem
 * Fetches document data from Redux and passes it to DocumentCard presentation component
 */
const DocumentSearchResultItem: React.FC<DocumentSearchResultItemProps> = ({
  documentId,
  relevance,
  contentType,
  score,
  confidence,
  reasoning,
  keyTerms
}) => {
  const { t } = useTranslation();
  // Get document from Redux state using proper selector
  const document = useAppSelector(state =>
    documentSelectOneObjectByQuery(state.DBDocumentReducer, { _id: documentId })
  );

  // Get knowledgebase from Redux state for legal domain
  const knowledgebase = useAppSelector(state =>
    document?.knowledgebaseId
      ? knowledgebaseSelectOneObjectByQuery(state.DBKnowledgebaseReducer, { _id: document.knowledgebaseId })
      : null
  );

  // Determine if this is a user-uploaded document
  const isUserUpload = !!knowledgebase?.caseId;

  // Build props for DocumentCard
  const documentTitle = document?.title || document?.heading || document?.name || t('common.fallbacks.untitledDocument', 'Untitled Document');

  // Ensure subtitle is different from title
  const documentSubtitle = isUserUpload
    ? undefined
    : document?.metadata?.source && document.metadata.source !== documentTitle
      ? document.metadata.source
      : undefined;

  const knowledgebaseName = knowledgebase?.name || '';
  const jurisdiction = knowledgebase?.jurisdiction || '';
  const documentUrl = document?.metadata?.document_url;

  return (
    <RelevantDocumentCard
      documentId={documentId}
      documentTitle={documentTitle}
      documentSubtitle={documentSubtitle}
      knowledgebaseName={knowledgebaseName}
      jurisdiction={jurisdiction}
      relevance={relevance}
      contentType={contentType}
      score={score || 0}
      confidence={confidence}
      reasoning={reasoning}
      keyTerms={keyTerms}
      showScore={true}
      isUserUpload={isUserUpload}
      documentUrl={documentUrl}
    />
  );
};

export default DocumentSearchResultItem;

